/**
 * CallRail 25-Month Data-Retention Pre-Flight (Story 22.1, AC #4; Round 2 Pre-mortem D)
 *
 * CallRail's Communication Records (calls, recordings, text messages, form
 * submissions, web visitor sessions) are auto-deleted after **25 months** per
 * `support.callrail.com/hc/en-us/articles/5711337552269-CallRail-s-data-retention-policy`
 * verified 2026-05-14. The docs are explicit:
 *
 *  > "If you make a request to an endpoint for Communication Records that are
 *  >  outside of the retention period, you will receive an error directing you
 *  >  to revise your date range. This includes requests using the `all_time`
 *  >  date parameter."
 *
 * Rather than absorb a round-trip + error parse + retry shaping, the adapter
 * **pre-flight rejects** any `fetchMetrics` call whose `dateRange.start` is
 * earlier than `now - 25 months` AT THE ADAPTER BOUNDARY, returning
 * `AdapterErrorCode.validation_error` with `retryable: false` and a
 * user-facing message — BEFORE making the API call.
 *
 * **This is the FIRST adapter in the ProvenLeap library with a hard-retention
 * pre-flight.** A future Story 22.3 (CallTrackingMetrics) may need a similar
 * pattern; the helper signature here is intentionally generic so it can be
 * lifted as the basis of a shared utility once we have a second consumer.
 *
 * Boundary behavior is INCLUSIVE — `dateRange.start === retentionCutoff` is
 * treated as PASS (the call is for the exact moment the data is still
 * retained; failing on `>=` instead of `>` would create a one-millisecond
 * window where the adapter rejects a request CallRail would have served).
 */

/**
 * The retention ceiling in months. Public so tests and the adapter's top
 * JSDoc can reference the canonical value without duplicating the literal.
 */
export const CALLRAIL_RETENTION_MONTHS = 25;

/**
 * Error class surfaced by the retention pre-flight. The adapter's
 * `fetchMetrics` orchestrator catches this and converts to a structured
 * `AdapterError` with `code: "invalid_request"` (mapped to
 * `validation_error` semantics per AC #4).
 *
 * Carries the computed `retentionCutoff` so the catch site can format a
 * user-facing message naming the earliest acceptable date.
 */
export class CallRailRetentionViolationError extends Error {
  readonly retentionCutoff: Date;
  readonly requestedStart: Date;

  constructor(retentionCutoff: Date, requestedStart: Date) {
    const cutoffIso = retentionCutoff.toISOString().slice(0, 10);
    super(
      `CallRail retains call data for ${CALLRAIL_RETENTION_MONTHS} months. Adjust your report date range to ${cutoffIso} or later.`,
    );
    this.name = "CallRailRetentionViolationError";
    this.retentionCutoff = retentionCutoff;
    this.requestedStart = requestedStart;
  }
}

/**
 * Computes the retention cutoff at a specific moment in time. Pure function
 * — safe to call from anywhere; takes `now` as an argument so tests can
 * supply a fixed clock.
 *
 * **Day-of-month clamping (Pass 1 review fix):** previously the helper
 * called `setUTCMonth(currentMonth - 25)` which does NOT clamp the day.
 * For `now = 2026-03-31`, the target month becomes February 2024; Feb
 * has 28 days, so the date rolls forward to **2024-03-02 or 03**.
 * Effect: retention cutoff was effectively LATER than 25 months ago,
 * rejecting valid date ranges on end-of-month dates (March/May/July/
 * October/December 31). This implementation clamps to the LAST day of
 * the target month when the source date overflows, restoring the
 * intended "calendar 25 months ago" semantics.
 *
 * Using UTC arithmetic is deliberate — the queue scheduler operates in
 * UTC, so the retention boundary is also UTC-anchored.
 */
export function computeCallRailRetentionCutoff(now: Date = new Date()): Date {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const day = now.getUTCDate();
  const hours = now.getUTCHours();
  const minutes = now.getUTCMinutes();
  const seconds = now.getUTCSeconds();
  const ms = now.getUTCMilliseconds();

  // Compute the target month index. Pass 1 review fix: do the
  // month-arithmetic manually so we can clamp the day BEFORE
  // constructing the Date — Date.UTC() with day=31 in a 28-day month
  // wraps to the next month (the original bug).
  const targetMonthAbsolute = year * 12 + month - CALLRAIL_RETENTION_MONTHS;
  const targetYear = Math.floor(targetMonthAbsolute / 12);
  const targetMonth = ((targetMonthAbsolute % 12) + 12) % 12;

  // Last day of the target month (UTC). Construct day-0 of the NEXT
  // month and read its UTC date — JS normalizes to the last day of
  // the prior month.
  const lastDayOfTargetMonth = new Date(
    Date.UTC(targetYear, targetMonth + 1, 0),
  ).getUTCDate();
  const clampedDay = Math.min(day, lastDayOfTargetMonth);

  return new Date(
    Date.UTC(targetYear, targetMonth, clampedDay, hours, minutes, seconds, ms),
  );
}

/**
 * Pre-flight: throws `CallRailRetentionViolationError` if the requested
 * `dateRange.start` falls before the 25-month retention cutoff. Returns
 * void on success.
 *
 * Boundary is INCLUSIVE — `start === retentionCutoff` passes.
 *
 * @param dateRange.start - The requested report-window start.
 * @param now - Optional clock injection for test determinism. Defaults to
 *   `new Date()`.
 */
export function validateCallRailDateRangeAgainstRetention(
  dateRange: { start: Date; end?: Date },
  now: Date = new Date(),
): void {
  // Pass 1 review fix: Invalid Date has `.getTime() === NaN`, and
  // `NaN < anything === false`, so the previous check silently passed
  // Invalid Date inputs through to the adapter's `toISOString()` call
  // which then threw `RangeError: Invalid time value` outside any
  // structured-error path. Surface the bad date as a clean retention
  // violation instead.
  const startMs = dateRange.start.getTime();
  if (!Number.isFinite(startMs)) {
    throw new CallRailRetentionViolationError(
      computeCallRailRetentionCutoff(now),
      dateRange.start,
    );
  }
  // Pass 2 review fix: also validate `end` when provided. An Invalid
  // `end` date would otherwise crash downstream at
  // `dateRange.end.toISOString()` outside any try/catch.
  if (dateRange.end !== undefined) {
    const endMs = dateRange.end.getTime();
    if (!Number.isFinite(endMs)) {
      throw new CallRailRetentionViolationError(
        computeCallRailRetentionCutoff(now),
        dateRange.end,
      );
    }
  }
  const cutoff = computeCallRailRetentionCutoff(now);
  if (startMs < cutoff.getTime()) {
    throw new CallRailRetentionViolationError(cutoff, dateRange.start);
  }
}
